import type { Room } from '@spike/room-model';
import type { BoardingOffer, BoardingService, BoardingServiceWrapper } from 'model/Service';
import { isBoardingDealOfferType } from './boardingDealOffers';

const isRateRuleOffer = (offer: BoardingOffer): boolean =>
    String(offer.offerType ?? '')
        .toLowerCase()
        .trim() === 'rate_rule';

export function getBoardingAppliedOfferForStandardPricing(appliedOffer?: BoardingOffer): BoardingOffer | undefined {
    if (!appliedOffer || isBoardingDealOfferType(appliedOffer)) {
        return undefined;
    }
    return appliedOffer;
}

export function hasBoardingRateRuleApplied(
    service: Pick<BoardingService, 'offers' | 'appliedOffer' | 'catalogPrice' | 'price'> & { beforePrice?: number }
): boolean {
    const appliedOffer = getBoardingAppliedOfferForStandardPricing(service.appliedOffer);
    if (appliedOffer?.offerType != null) {
        return isRateRuleOffer(appliedOffer);
    }
    return false;
}

/** Original/catalog nightly before rate_rule adjustments (for alerts and beforePrice). */
export function getBoardingCatalogNightlyPrice(
    service: Pick<BoardingService, 'price' | 'offers' | 'catalogPrice'> & { beforePrice?: number }
): number {
    // Catalog is independent from applied_offer; it's the baseline price (pre rule/rate).
    return service.catalogPrice ?? service.beforePrice ?? service.price ?? 0;
}

/** Prefer non-empty offers from Redux; avoid replacing with an empty array from API. */
export function mergeBoardingOffers(
    latestOffers?: BoardingOffer[],
    fallbackOffers?: BoardingOffer[]
): BoardingOffer[] | undefined {
    const fromLatest = latestOffers ?? [];
    const fromFallback = fallbackOffers ?? [];
    if (fromLatest.length > 0) {
        return fromLatest;
    }
    return fromFallback.length > 0 ? fromFallback : undefined;
}

export function getBoardingStandardNightlyPrice(
    service: Pick<BoardingService, 'price' | 'offers' | 'appliedOffer' | 'catalogPrice'> & {
        beforePrice?: number;
    }
): number {
    const appliedOffer = getBoardingAppliedOfferForStandardPricing(service.appliedOffer);
    if (appliedOffer?.pricePerNight != null && Number.isFinite(appliedOffer.pricePerNight)) {
        return appliedOffer.pricePerNight;
    }
    return service.catalogPrice ?? service.price ?? 0;
}

/** Room occupancy from fetchBoardingOffersThunk; always prefer latest catalog over stale service.rooms. */
export function syncBoardingRoomsFromOffersCatalog(
    service: BoardingServiceWrapper,
    boardingServices: BoardingService[],
    currentRoom?: Room
): { service: BoardingServiceWrapper; room: Room } | null {
    const latest = boardingServices.find(s => s.serviceId === service.serviceId);
    const freshRooms = latest?.rooms;
    if (!freshRooms?.length) {
        return null;
    }
    const freshRoom = freshRooms[0];
    if (currentRoom === freshRoom && service.rooms === freshRooms) {
        return null;
    }
    return {
        service: { ...service, rooms: freshRooms },
        room: freshRoom
    };
}

export function preserveRoomUnitIdAfterRoomSync(
    roomSync: { room: Room } | null,
    roomUnitId: number | undefined,
    room: Room | undefined
): number | undefined {
    if (!roomSync) {
        return roomUnitId;
    }
    if (roomUnitId == null) {
        return undefined;
    }
    const units = room?.units;
    if (!units?.length) {
        return undefined;
    }
    return units.some(u => u.id === roomUnitId) ? roomUnitId : undefined;
}

export function resolveBoardingStandardRateSelection(
    service: BoardingServiceWrapper,
    boardingServices: BoardingService[]
): { service: BoardingServiceWrapper; nightlyPrice: number } {
    const latest = boardingServices.find(s => s.serviceId === service.serviceId);
    const mergedOffers = mergeBoardingOffers(latest?.offers, service.offers);
    const merged: BoardingServiceWrapper = latest
        ? {
              ...service,
              ...latest,
              offers: mergedOffers,
              rooms: latest.rooms ?? service.rooms,
              appliedOffer: latest.appliedOffer
          }
        : { ...service, offers: mergedOffers, appliedOffer: service.appliedOffer };

    const catalogPrice = latest?.catalogPrice ?? latest?.price ?? merged.catalogPrice ?? merged.beforePrice ?? 0;
    const standardAppliedOffer = getBoardingAppliedOfferForStandardPricing(merged.appliedOffer);
    const pricingInput = {
        price: catalogPrice,
        catalogPrice,
        beforePrice: merged.beforePrice,
        offers: mergedOffers,
        appliedOffer: standardAppliedOffer
    };
    const catalogNightly = getBoardingCatalogNightlyPrice(pricingInput);
    const appliedNightly = getBoardingStandardNightlyPrice(pricingInput);

    return {
        service: {
            ...merged,
            catalogPrice: latest?.catalogPrice ?? merged.catalogPrice ?? catalogPrice,
            beforePrice: catalogNightly,
            price: appliedNightly,
            appliedOffer: standardAppliedOffer,
            membershipItem: undefined,
            enrollment: undefined,
            selectedOffer: undefined
        },
        nightlyPrice: appliedNightly
    };
}
